import { useMemo } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ScheduleTypes } from '@frontend/api-schedule';
import type { AllDayCalendarEvent, CalendarColumnGroup, ProviderCalendarColumn } from '@frontend/calendar-grid';
import { formatDate } from '@frontend/date';
import { getFullName } from '@frontend/name';
import { useAppScopeStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import { GridCalendarEvent } from '../components/calendar-view/types';
import { isAllDayEvent } from '../components/calendar-view/utils';
import { useCalendarLocationIds } from '../stores/calendar-location.store';
import { useCalendarStore } from '../stores/calendar.store';
import {
  buildBreakEvents,
  buildExceptionGridEvent,
  buildV2AppointmentGridEvent,
} from './calendar-grid-v2-event-builders';
import { applyColumnOrder } from './scheduler-v3/calendar-grid-v3-event-builders';
import { useGetCalendarV2QueriesData } from './use-get-calendar-v2-queries-data';

dayjs.extend(utc);
dayjs.extend(timezone);

const checkIsUnassignedProvider = (
  appointment: ReturnType<typeof useGetCalendarV2QueriesData>['appointments'][number],
  providerId: string,
  providerDisplayName: string
): boolean => {
  return (
    !appointment.practitionerName &&
    !!appointment.providerIds?.length &&
    appointment?.providerIds[0] === '' &&
    providerId === `unassigned-${appointment.locationId}` &&
    providerDisplayName === 'unassigned'
  );
};

const formatForSorting = (str: string) => str.replace(/[^a-zA-Z0-9\s]/g, ' ').toLowerCase();
export const getProviderName = (provider: ScheduleTypes.SchemaProvider) => {
  return getFullName(provider) || provider.publicDisplayName?.trim() || provider.resourceName || '';
};

const sortProvidersByName = (providers: ScheduleTypes.SchemaProvider[]): ScheduleTypes.SchemaProvider[] => {
  // Separate unassigned providers from regular providers
  const regularProviders = providers.filter((provider) => provider.publicDisplayName !== 'unassigned');
  const unassignedProviders = providers.filter((provider) => provider.publicDisplayName === 'unassigned');

  // Sort only the regular providers alphabetically, then append unassigned providers at the end
  const sortedRegularProviders = regularProviders.sort((a, b) => {
    // Old Name used for sorting: a.publicDisplayName || a.lastName || a.firstName || a.resourceName || ''
    const aName = formatForSorting(getProviderName(a));
    const bName = formatForSorting(getProviderName(b));
    return aName.localeCompare(bName);
  });

  return [...sortedRegularProviders, ...unassignedProviders];
};

export type CalendarGridV2Props = Pick<
  ReturnType<typeof useGetCalendarV2QueriesData>,
  | 'filteredAppointments'
  | 'filteredProviderList'
  | 'providerExceptions'
  | 'providersOfficeHours'
  | 'officeHours'
  | 'officeExceptions'
  | 'isLoading'
>;

type CalendarGridV2Data = {
  headerType: 'provider';
  columns: ProviderCalendarColumn[];
  events: GridCalendarEvent[];
  allDayEvents: AllDayCalendarEvent[];
  columnGroups: CalendarColumnGroup[];
  calendarDate: string;
};

export const useCalendarGridV2Data = ({
  filteredAppointments,
  filteredProviderList,
  providerExceptions,
  providersOfficeHours,
  officeHours,
  officeExceptions,
}: CalendarGridV2Props): CalendarGridV2Data => {
  const { calendarDate } = useCalendarStore(['calendarDate']);
  const calendarDateDay = dayjs(calendarDate)
    .format('dddd')
    .toLowerCase() as keyof ScheduleTypes.OfficeHoursWeekSchedule;
  const { calendarLocationIds } = useCalendarLocationIds();
  const { getScopeName } = useAppScopeStore();
  const [columnEntityOrder] = usePreference('ScheduleCalendarViewColumnOrder');

  const sortedProviders = useMemo(() => sortProvidersByName(filteredProviderList), [filteredProviderList]);

  return useMemo<CalendarGridV2Data>(() => {
    const columns: ProviderCalendarColumn[] = [];
    const events: GridCalendarEvent[] = [];
    const allDayEvents: AllDayCalendarEvent[] = [];
    const addedGroupAllDayExceptionIds = new Set<string>(); // office exceptions are evaluated per-provider, dedupe to one group-scoped entry
    const columnGroups: CalendarColumnGroup[] = [];

    let groupOffset = 0;

    for (const locationId of calendarLocationIds) {
      const locationProviders = sortedProviders.filter((p) => p.locationId === locationId);

      const columnIds: string[] = [];

      for (const provider of locationProviders) {
        const columnId = provider.id ?? '';
        if (!columnId) continue;
        const columnEventIds: string[] = [];
        const providerDisplayName = provider.publicDisplayName?.trim() ?? '';
        const providerName = getProviderName(provider);

        //#region Office Breaks & Exceptions

        // Office Breaks
        const locationOfficeHours = officeHours?.find((office) => office.locationId === locationId)?.officeHours;
        const selectedDayOfficeHours = locationOfficeHours?.[calendarDateDay];
        selectedDayOfficeHours?.forEach((officeHour) => {
          const { events: breakEvts, ids: breakIds } = buildBreakEvents({
            breaks: officeHour.breaks ?? [],
            dayKey: calendarDateDay,
            startDate: calendarDate,
            calendarDate,
            locationId,
            getScopeName,
            // Office exceptions are not tied to a specific provider, so entityId is undefined
            entityId: undefined,
            entityName: undefined,
          });
          events.push(...breakEvts);
          columnEventIds.push(...breakIds);
        });

        // Office Exceptions
        const selectedLocationOfficeExceptions = officeExceptions.filter(
          (ex) => ex.locationId === locationId && !ex.isAvailable && !!ex.id
        );
        selectedLocationOfficeExceptions.forEach((ex) => {
          const startHour = dayjs(ex.startDateTime).format('HH:mm');
          const endHour = dayjs(ex.endDateTime).format('HH:mm');
          const exceptionId = ex.id ?? '';

          if (isAllDayEvent(startHour, endHour)) {
            if (!addedGroupAllDayExceptionIds.has(exceptionId)) {
              addedGroupAllDayExceptionIds.add(exceptionId);
              allDayEvents.push({
                id: exceptionId,
                name: ex.name?.trim() || 'Out of Office',
                scope: 'group',
                groupId: locationId,
                startDate: formatDate(ex.startDateTime, 'YYYY-MM-DD'),
                endDate: formatDate(ex.endDateTime, 'YYYY-MM-DD'),
              });
            }
            return;
          }

          events.push(
            buildExceptionGridEvent({
              id: exceptionId,
              title: ex.name?.trim() || 'Out of Office',
              startHour,
              endHour,
              startDate: calendarDate,
              calendarDate,
              locationId,
              getScopeName,
              // Office exceptions are not tied to a specific provider, so entityId is undefined
              entityId: undefined,
              entityName: undefined,
            })
          );
          columnEventIds.push(exceptionId);
        });

        //#endregion

        //#region Provider Breaks & Exceptions

        // Provider Breaks
        const providerOfficeHours = providersOfficeHours?.filter((p) => p.providerId === columnId);
        providerOfficeHours.forEach((providerOfficeHour) => {
          const { events: breakEvts, ids: breakIds } = buildBreakEvents({
            breaks: providerOfficeHour.breaks ?? [],
            dayKey: providerOfficeHour.dayOfWeek ?? '',
            startDate: calendarDate,
            calendarDate,
            locationId,
            getScopeName,
            entityId: columnId,
            entityName: providerName,
          });
          events.push(...breakEvts);
          columnEventIds.push(...breakIds);
        });

        // Provider Exceptions
        const selectedProviderExceptions = providerExceptions.filter(
          (ex) => ex.providerId === columnId && !ex.isAvailable && !!ex.id
        );
        selectedProviderExceptions.forEach((ex) => {
          const startHour = dayjs(ex.startDateTime).format('HH:mm');
          const endHour = dayjs(ex.endDateTime).format('HH:mm');
          const exceptionId = ex.id ?? '';

          if (isAllDayEvent(startHour, endHour)) {
            allDayEvents.push({
              id: exceptionId,
              name: ex.name?.trim() || 'Unavailable',
              scope: 'column',
              groupId: locationId,
              columnId,
              startDate: formatDate(ex.startDateTime, 'YYYY-MM-DD'),
              endDate: formatDate(ex.endDateTime, 'YYYY-MM-DD'),
            });
            return;
          }

          events.push(
            buildExceptionGridEvent({
              id: exceptionId,
              title: ex.name?.trim() || 'Unavailable',
              startHour,
              endHour,
              startDate: calendarDate,
              calendarDate,
              locationId,
              getScopeName,
              entityId: columnId,
              entityName: providerName,
            })
          );
          columnEventIds.push(exceptionId);
        });

        //#endregion

        const providerAppointments = filteredAppointments.filter((appt) => {
          const isUnassignedProvider = checkIsUnassignedProvider(appt, columnId, providerDisplayName);
          if (isUnassignedProvider) {
            return columnId === `unassigned-${locationId}` && providerDisplayName === 'unassigned';
          } else {
            return (
              (!!appt.providerIds?.length || providerName) &&
              appt.locationId === locationId &&
              appt.providerIds?.some((id) => {
                const eventPractitionerName = appt.practitionerName?.trim();
                if (id && columnId) {
                  return id === columnId;
                } else if (providerName && eventPractitionerName === providerName.trim()) {
                  return true;
                } else if (providerDisplayName && eventPractitionerName === providerDisplayName.trim()) {
                  return true;
                }
                return false;
              })
            );
          }
        });

        providerAppointments.forEach((appt) => {
          events.push(buildV2AppointmentGridEvent(appt, getScopeName, columnId, providerName, calendarDate));
          columnEventIds.push(appt.id ?? '');
        });

        columns.push({
          id: columnId,
          name: providerName,
          providerImage: provider.publicDisplayImage || '',
          eventIds: columnEventIds,
          count: providerAppointments.length,
          groupId: locationId,
        });

        columnIds.push(columnId);
      }

      const hasEventsInGroup = columnIds.some(
        (colId) => columns.find((col) => col.id === colId)?.eventIds.length ?? 0 > 0
      );
      columnGroups.push({
        id: locationId,
        name: calendarLocationIds.length > 1 ? getScopeName(locationId) : '',
        columnIds: applyColumnOrder(columnIds, columnEntityOrder?.provider?.[locationId]),
        groupOffset,
        emptyStateVariant: columnIds.length === 0 ? 'no-columns' : !hasEventsInGroup ? 'no-events' : undefined,
      });

      groupOffset += columnIds.length;
    }

    return {
      headerType: 'provider',
      columns,
      events,
      allDayEvents,
      columnGroups,
      calendarDate,
    } satisfies CalendarGridV2Data;
  }, [
    calendarLocationIds,
    sortedProviders,
    getScopeName,
    calendarDate,
    officeHours,
    calendarDateDay,
    officeExceptions,
    providersOfficeHours,
    providerExceptions,
    filteredAppointments,
    columnEntityOrder,
  ]);
};
